import './PhotoEditor.css';
import React, { forwardRef, useImperativeHandle } from 'react';
import { TABS, TOOLS } from 'react-filerobot-image-editor';
import { Container, Editor, SaveCopyIcon } from './PhotoEditor.styles';
import { IEditedImage, IPhotoEditorProps } from './PhotoEditor.types';
import { editorTranslations } from './Translations';
import { toast } from 'react-toastify';
import { useMutateInspection } from 'hooks/Inspections/InspectionsHooks';
import { usePhotoEditor } from './hooks/usePhotoEditor';
import { useInspectionPhotos } from './hooks/useInspectionPhotos';

export interface IPhotoEditorRef {
  openImageEditor: () => Promise<void>;
}

const PhotoEditor = forwardRef<IPhotoEditorRef, IPhotoEditorProps>(
  ({ inspection, getCurrentPhoto, closeGallery }, ref) => {
    const { invalidateInspectionParallel } = useMutateInspection();
    const { uploadPhoto } = useInspectionPhotos({ inspection });
    const { currentImage, showEditor, loading, openEditor, closeEditor, setIsLoading } = usePhotoEditor({
      getCurrentPhoto,
    });

    useImperativeHandle(ref, () => ({
      openImageEditor: openEditor,
    }));

    const handleSave = async (editedImage: IEditedImage) => {
      if (loading) return;

      try {
        setIsLoading(true);
        await uploadPhoto(editedImage.imageBase64, editedImage.mimeType, `copy-${editedImage.fullName}`);

        invalidateInspectionParallel(inspection.id);
        closeGallery();
        closeEditor();
        toast.success('Foto editada com sucesso!');
      } catch (error) {
        console.error('Erro ao salvar imagem:', error);
        toast.error('Falha ao salvar a foto editada.');
      } finally {
        setIsLoading(false);
      }
    };

    if (!showEditor || !currentImage) {
      return null;
    }

    return (
      <Container open={showEditor} onClose={closeEditor}>
        <Editor
          source={currentImage}
          onClose={closeEditor}
          annotationsCommon={{
            fill: '#00000000',
            stroke: '#ff0000',
            strokeWidth: 3,
          }}
          Text={{ text: 'Digite e aperte a tecla Enter para salvar...', fontSize: 32, fill: '#fff', strokeWidth: 0 }}
          showBackButton={true}
          translations={editorTranslations}
          Rotate={{ angle: 90, componentType: 'slider' }}
          tabsIds={[TABS.FILTERS, TABS.ANNOTATE, TABS.ADJUST, TABS.FINETUNE]}
          defaultTabId={TABS.FILTERS}
          defaultToolId={TOOLS.TEXT}
          savingPixelRatio={2}
          previewPixelRatio={2}
          closeAfterSave={true}
          disableSaveIfNoChanges={true}
          defaultSavedImageName={'edited-image'}
          defaultSavedImageQuality={1.0}
          defaultSavedImageType="jpeg"
          moreSaveOptions={[
            {
              label: loading ? 'Salvando...' : 'Salvar cópia',
              onClick: (_, triggerSave) =>
                triggerSave((editedImage: IEditedImage) => {
                  handleSave(editedImage);
                }),
              icon: () => <SaveCopyIcon />,
            },
            {
              label: loading ? 'Salvando...' : 'Salvar cópia',
              onClick: (_, triggerSave) =>
                triggerSave((editedImage: IEditedImage) => {
                  handleSave(editedImage);
                }),
              icon: () => <SaveCopyIcon />,
            },
          ]}
        />
      </Container>
    );
  }
);

export default PhotoEditor;
